/**
 * PlaybackFacade — long-term public transport API for TPG Player.
 *
 * Applications and licensees should speak to TPG Player through this surface.
 * Future playback engine replacements must not require host application changes.
 *
 * Sprint 8 Phase B1: Media Asset + presentation strategy awareness (optional).
 * Production NextGenPlayer continues to use the same factory.
 *
 * @param {object} engine createPlaybackEngine() instance
 * @param {object} [casting] optional CastingManager
 * @param {object} [options]
 * @param {object} [options.mediaAsset]
 * @param {object} [options.strategy]  from resolvePresentationStrategy
 * @param {string[]} [options.capabilities]  enabled capability module ids
 */
export function createPlaybackFacade(engine, casting, options) {
  options = options || {};
  var mediaAsset = options.mediaAsset || null;
  var strategy = options.strategy || null;
  var capabilities = Array.isArray(options.capabilities)
    ? options.capabilities.slice()
    : [];

  function transport() {
    return casting && casting.isRemote && casting.isRemote() ? casting : engine;
  }

  return {
    play: function () {
      return transport().play();
    },
    pause: function () {
      return transport().pause();
    },
    seek: function (seconds) {
      return transport().seek(seconds);
    },
    getCurrentTime: function () {
      var st = engine.getState ? engine.getState() : {};
      return st.currentTime || 0;
    },
    getDuration: function () {
      var st = engine.getState ? engine.getState() : {};
      return st.duration || 0;
    },
    setVolume: function (v) {
      return transport().setVolume
        ? transport().setVolume(v)
        : engine.setVolume(v);
    },
    setMuted: function (m) {
      var t = transport();
      return t.setMuted ? t.setMuted(m) : engine.setMuted(m);
    },
    setPlaybackRate: function (r) {
      return engine.setPlaybackRate(r);
    },
    enterFullscreen: function (el) {
      var st = engine.getState ? engine.getState() : {};
      if (st.fullscreen) return Promise.resolve();
      return engine.toggleFullscreen(el);
    },
    exitFullscreen: function (el) {
      var st = engine.getState ? engine.getState() : {};
      if (!st.fullscreen) return Promise.resolve();
      return engine.toggleFullscreen(el);
    },
    destroy: function () {
      if (engine && engine.destroy) engine.destroy();
      mediaAsset = null;
      strategy = null;
    },
    isReady: function () {
      var st = engine.getState ? engine.getState() : {};
      var s = st.playbackState;
      return (
        s === "ready" ||
        s === "playing" ||
        s === "paused" ||
        s === "buffering" ||
        s === "seeking"
      );
    },
    getState: function () {
      return engine.getState ? engine.getState() : {};
    },
    getCapabilities: function () {
      return engine.getCapabilities ? engine.getCapabilities() : null;
    },
    /** @returns {object|null} current Media Asset */
    getMediaAsset: function () {
      return mediaAsset;
    },
    /**
     * Attach / replace Media Asset metadata (does not auto-load media).
     * Hosts load sources via existing engine APIs; asset drives presentation.
     * @param {object|null} asset
     * @param {object|null} [nextStrategy]
     */
    setMediaAsset: function (asset, nextStrategy) {
      mediaAsset = asset || null;
      if (nextStrategy) strategy = nextStrategy;
      return mediaAsset;
    },
    getPresentationStrategy: function () {
      return strategy;
    },
    setPresentationStrategy: function (next) {
      strategy = next || null;
      return strategy;
    },
    getEnabledCapabilities: function () {
      return capabilities.slice();
    },
    setEnabledCapabilities: function (ids) {
      capabilities = Array.isArray(ids) ? ids.slice() : [];
      return capabilities.slice();
    },
    hasCapability: function (id) {
      return capabilities.indexOf(id) !== -1;
    },
    /** @private underlying engine for advanced plugins */
    _engine: engine,
  };
}

export default createPlaybackFacade;
